import { Badge, Box, Flex, HStack, Image, Stack, Text } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";

import useFormatter from "@/hooks/useFormatter";
import { normalizePhotoUrl } from "@/utilities/helpers/normalizePhotoUrl";

// Fallback for listings that arrive with no `photo` field. The previous
// bgImage approach silently swallowed undefined URLs (browser dropped the
// invalid `url("undefined")`); a real <img> would otherwise render the
// browser's native broken-image icon.
const NO_IMAGE = "https://media.grupoguia.mx/assets/home-page/fichas/nodisponible.webp";

function FeaturedPropCard({
    id,
    operation,
    address,
    title,
    groundSize,
    bedrooms,
    bathrooms,
    parkingLots,
    price,
    presale,
    sale,
    lease,
    wiggotCode,
    subTitle,
    link,
    imageUrl,
    propertyType = null,
    isCentered,
    session,
    property_id,
    savePropsFavorites,
    userFavorites,
    isForMap = false,
    isForCity = false,
    currency = "MXN",
}) {
    const router = useRouter();
    const { NumberF } = useFormatter();
    // Normalized here rather than at each call site so every surface that
    // renders this card — the search grid, the map InfoWindow and all the
    // city landing pages — is covered by one change.
    const [imageFailed, setImageFailed] = useState(false);
    const heroSrc = (!imageFailed && normalizePhotoUrl(imageUrl)) || NO_IMAGE;

    const getOperationText = () => {
        return [presale && "Preventa", sale && "Venta", lease && "Renta"].filter(Boolean);
    };

    const sendToPropertyPage = (propertyId) => {
        if (!propertyId) {
            console.error("No property ID available");
            return;
        }
        if (!isForMap)
            router.push(
                `/Propiedad/wig-${propertyId}?operation=${presale ? "presale" : sale ? "sale" : "lease"}`
            );
        else
            window.open(
                `/Propiedad/wig-${propertyId}?operation=${presale ? "presale" : sale ? "sale" : "lease"}`,
                "_blank"
            );
    };

    //Validar si el desarrollo es favorito
    const isFavorite = useMemo(() => {
        return userFavorites?.some((fav) => fav.property_id === property_id);
    }, [userFavorites]);

    return (
        <LazyLoadComponent id={id}>
            <Box role="list" justifySelf="center">
                <Box
                    mx="auto"
                    display="flex"
                    w={
                        isForMap
                            ? { base: "250px", md: "350px" }
                            : isForCity
                              ? { base: "85vw", md: "300px" }
                              : { base: "320px", sm: "20.83vw" }
                    }
                    h={isForMap || isForCity ? "450px" : "50vh"}
                    as="div"
                    animate={isCentered ? { scale: 1.1 } : {}}
                    mt={0}
                    borderRadius="10px"
                    boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
                    overflow="hidden"
                    onClick={() => wiggotCode && sendToPropertyPage(wiggotCode)}
                    role="listitem"
                    aria-label={`Propiedad ${title}`}
                    position="relative"
                    _focus={{
                        outline: "2px solid",
                        outlineColor: "blue.500",
                        outlineOffset: "2px",
                    }}
                >
                    {/* Hero image as a real <img> instead of CSS bgImage so
                        we pick up native lazy-loading and async decode —
                        bgImage downloads eagerly and decodes on the main
                        thread, which was the dominant source of scroll lag
                        on the results grid. Positioned absolute behind the
                        text content; the white card body (h="55%" with
                        bg="#fff") covers the bottom half. */}
                    <Image
                        src={heroSrc}
                        alt={title || ""}
                        loading="lazy"
                        decoding="async"
                        // Only catches URLs that fail outright. A URL that
                        // 404s with an image body still fires `load` — see
                        // normalizePhotoUrl for that case.
                        onError={() => setImageFailed(true)}
                        position="absolute"
                        inset={0}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        zIndex={0}
                    />
                    <Flex direction="column" w="100%" position="relative" zIndex={1}>
                        <Flex direction="column" width="full" h="45%" cursor="pointer">
                            {/* Single row pinned to the top of the image
                                area, mirroring CardDevs: status badges on
                                the left (wrap when narrow), favorite on the
                                right (flexShrink=0 — corner control). No
                                kind marker since every FeaturedPropCard is a
                                resale property, not a development. */}
                            <Flex justify="space-between" align="flex-start" gap={2} px={3} mt={4}>
                                <Flex
                                    gap={2}
                                    align="center"
                                    flexWrap="wrap"
                                    minW={0}
                                    flex="1 1 auto"
                                >
                                    {getOperationText()?.map((item, index) => (
                                        <Badge
                                            key={index}
                                            px={2}
                                            py={1.5}
                                            color="gray.100"
                                            bg="accent.500"
                                            fontWeight="bold"
                                            boxShadow="lg"
                                            borderRadius="lg"
                                            fontSize="xs"
                                            textTransform="capitalize"
                                        >
                                            {item}
                                        </Badge>
                                    ))}
                                </Flex>
                                <Flex align="center" gap={2} flexShrink={0}>
                                    {session?.contact_id && (
                                        <Image
                                            src={
                                                isFavorite
                                                    ? "https://media.grupoguia.mx/assets/apps-icons/favorite_active.svg"
                                                    : "https://media.grupoguia.mx/assets/apps-icons/favorite.svg"
                                            }
                                            alt="favorite"
                                            maxH={isFavorite ? "4vh" : "5vh"}
                                            cursor="pointer"
                                            borderRadius="full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                savePropsFavorites(property_id, null);
                                            }}
                                        />
                                    )}
                                </Flex>
                            </Flex>
                        </Flex>
                        <Box
                            position="relative"
                            w="full"
                            h="55%"
                            cursor="pointer"
                            overflow="hidden"
                            borderBottomRadius="10px"
                            background="white"
                        >
                            <Box
                                px="10px"
                                textAlign="left"
                                paddingTop="3"
                                paddingBottom="0"
                                bg="#fff"
                                height="80%"
                                width="100%"
                                zIndex={2}
                                position="relative"
                            >
                                <Text
                                    textTransform="capitalize"
                                    color="#002B49"
                                    lineClamp="2"
                                    textAlign="center"
                                    fontWeight="600"
                                    fontSize="lg"
                                    truncate
                                >
                                    {title?.toLowerCase() || "Sin título"}
                                </Text>
                                <Stack py={1} h="3rem">
                                    <Text
                                        fontSize="sm"
                                        color="#4B4B4B"
                                        fontWeight={400}
                                        textTransform="capitalize"
                                        lineClamp={2}
                                        textAlign="center"
                                    >
                                        {address || "Dirección no disponible"}
                                    </Text>
                                </Stack>
                                <HStack
                                    spacing={5}
                                    mt={2}
                                    mb={3}
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    {groundSize ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cinta-metrica.svg"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                                alt="ground-size icon"
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {groundSize > 0 && <>{groundSize} m&sup2;</>}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                    {bedrooms ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/cama.svg"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                                alt="bedrooms icon"
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {bedrooms > 0 && <>{bedrooms}</>}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                    {bathrooms ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/inodoro.svg"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                                alt="bathrooms icon"
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {bathrooms > 0 && <>{bathrooms}</>}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                    {parkingLots ? (
                                        <Flex>
                                            <Image
                                                src="https://media.grupoguia.mx/assets/home-page/icons/property-card/carro-familiar.svg"
                                                boxSize="20px"
                                                display="inline-flex"
                                                mr={2}
                                                alt="parking-lot icons"
                                            />
                                            <Text
                                                color="#949494"
                                                fontSize={{ base: "11px", md: "12px" }}
                                                fontWeight="400"
                                            >
                                                {parkingLots > 0 && <>{parkingLots}</>}
                                            </Text>
                                        </Flex>
                                    ) : null}
                                </HStack>
                                <Text
                                    my={1}
                                    display="block"
                                    fontSize="sm"
                                    lineHeight="normal"
                                    fontWeight={600}
                                    href={link}
                                    target="_blank"
                                    color="#000"
                                    pb={2}
                                >
                                    {subTitle}
                                </Text>
                                {price ? (
                                    <Flex mt={-2} alignItems="center" justifyContent="center">
                                        <Text color="#000000" fontWeight={600} fontSize="sm">
                                            Desde ${NumberF(price)} {currency}
                                        </Text>
                                    </Flex>
                                ) : null}
                            </Box>
                            <Box
                                height="20%"
                                bg="#DFDFDF"
                                width="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                position="relative"
                                zIndex={2}
                                borderBottomRadius="10px"
                            >
                                <Text
                                    fontSize="sm"
                                    fontWeight={400}
                                    textAlign="center"
                                    width="100%"
                                >
                                    {propertyType}
                                </Text>
                            </Box>
                        </Box>
                    </Flex>
                </Box>
            </Box>
        </LazyLoadComponent>
    );
}

export default FeaturedPropCard;
